import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  type ClipboardEvent,
} from 'react';
import { gsap } from 'gsap';
import { RM } from '../lib/rm';
import type { Attachment } from '../lib/attachments';

export type ComposerHandle = { focus: () => void };

type ComposerProps = {
  value: string;
  onChange: (v: string) => void;
  // Fires on send with the field's bounding rect (used for the message FLIP-lift).
  onSend: (fieldRect: DOMRect) => void;
  busy: boolean;
  attachments: Attachment[];
  onAddFiles: (files: File[]) => void;
  onRemoveAttachment: (index: number) => void;
};

export const Composer = forwardRef<ComposerHandle, ComposerProps>(function Composer(
  { value, onChange, onSend, busy, attachments, onAddFiles, onRemoveAttachment },
  ref,
) {
  const fieldRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const sendRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({ focus: () => taRef.current?.focus() }), []);

  // Auto-grow the textarea up to its max-height. Guard on clientWidth so a
  // pre-layout / zero-width mount can't bake in a wrapped-placeholder height;
  // recompute on resize once the real width is known (and as wrap points move).
  const autoGrow = useCallback(() => {
    const ta = taRef.current;
    if (!ta || !ta.clientWidth) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 180) + 'px';
  }, []);

  useLayoutEffect(autoGrow, [value, autoGrow]);

  useEffect(() => {
    window.addEventListener('resize', autoGrow);
    return () => window.removeEventListener('resize', autoGrow);
  }, [autoGrow]);

  // Magnetic pull toward the pointer when it's near the send button.
  useEffect(() => {
    if (RM) return;
    const btn = sendRef.current;
    if (!btn) return;
    const magX = gsap.quickTo(btn, 'x', { duration: 0.4, ease: 'power3' });
    const magY = gsap.quickTo(btn, 'y', { duration: 0.4, ease: 'power3' });
    const onMove = (e: PointerEvent) => {
      const r = btn.getBoundingClientRect();
      const bx = r.left + r.width / 2;
      const by = r.top + r.height / 2;
      const dx = e.clientX - bx;
      const dy = e.clientY - by;
      if (Math.hypot(dx, dy) < 90) {
        magX(dx * 0.28);
        magY(dy * 0.28);
      } else {
        magX(0);
        magY(0);
      }
    };
    window.addEventListener('pointermove', onMove);
    return () => window.removeEventListener('pointermove', onMove);
  }, []);

  const armed = value.trim().length > 0 || attachments.length > 0;

  const submit = () => {
    if (!armed || busy || !fieldRef.current) return;
    onSend(fieldRef.current.getBoundingClientRect());
  };

  // Screenshots are usually pasted, not picked from a file dialog — grab any
  // image straight off the clipboard.
  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'));
    if (files.length > 0) {
      e.preventDefault();
      onAddFiles(files);
    }
  };

  return (
    <div className="composer">
      {attachments.length > 0 && (
        <div className="attach-strip">
          {attachments.map((a, i) => (
            <div className="attach-thumb" key={i}>
              <img src={a.url} alt={a.name || 'attached image'} />
              <button
                type="button"
                className="attach-remove"
                aria-label="Remove image"
                onClick={() => onRemoveAttachment(i)}
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="field" ref={fieldRef}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            if (files.length > 0) onAddFiles(files);
            e.target.value = '';
          }}
        />
        <button
          type="button"
          className="attach"
          title="Attach image"
          aria-label="Attach image"
          onClick={() => fileInputRef.current?.click()}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9}>
            <path d="M21.44 11.05l-9.19 9.19a5 5 0 0 1-7.07-7.07l9.19-9.19a3 3 0 0 1 4.24 4.24l-9.2 9.19a1 1 0 0 1-1.41-1.42l8.49-8.48" />
          </svg>
        </button>
        <textarea
          ref={taRef}
          className="ta"
          rows={1}
          placeholder="Ask..."
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onPaste={onPaste}
          // A large paste (a few thousand characters) into a spellchecked
          // textarea can freeze Safari for several seconds — WebKit
          // re-scans the whole field synchronously on every change. Off by
          // default; nobody's typing prose that needs live spellcheck here.
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          data-gramm="false"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <button
          ref={sendRef}
          className={armed ? 'send live' : 'send'}
          title="Send"
          disabled={!armed || busy}
          onClick={submit}
          aria-label="Send"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9}>
            <path d="M5 12h14" />
            <path d="M13 6l6 6-6 6" />
          </svg>
        </button>
      </div>
    </div>
  );
});
